import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPin, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const LEEG = { naam: "", straat: "", huisnummer: "", postcode: "", plaats: "", land: "Nederland", notitie: "" };
const VELDEN = [["naam", "Naam locatie"], ["straat", "Straat"], ["huisnummer", "Huisnummer"],
  ["postcode", "Postcode"], ["plaats", "Plaats"], ["land", "Land"]];

export const Bezorglocaties = ({ isAdmin }) => {
  const [items, setItems] = useState([]);
  const [bedrijf, setBedrijf] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/instellingen/locaties").then(({ data }) => {
    setBedrijf((data.items || []).find(x => x.id === "bedrijf") || null);
    setItems((data.items || []).filter(x => x.id !== "bedrijf"));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (i, k, v) => setItems(prev => prev.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/instellingen/locaties", { items: items.filter(x => (x.naam || "").trim()) });
      setItems((data.items || []).filter(x => x.id !== "bedrijf"));
      toast.success("Bezorglocaties opgeslagen");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Alleen beheerders kunnen dit wijzigen" : "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="bezorglocaties">
      <h2 className="font-display text-xl font-medium flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> Bezorglocaties</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        Eigen adressen waar leveranciers naartoe kunnen leveren. Alleen leveringen op een eigen locatie worden ingeboekt
        in je voorraad; gaat een inkooporder naar een klant of ander adres, dan hoeft die niet ingeboekt te worden.
      </p>
      {bedrijf && (
        <div className="rounded-lg border border-border bg-secondary/40 p-3 mb-4 text-sm" data-testid="locatie-bedrijfsadres">
          <div className="font-medium">{bedrijf.naam || "Bedrijfsadres"} <span className="text-xs text-muted-foreground">· standaard</span></div>
          <pre className="whitespace-pre-wrap font-sans text-xs text-muted-foreground mt-1">{bedrijf.adres}</pre>
          <p className="text-[11px] text-muted-foreground mt-1">Beheer je hierboven bij Bedrijfsgegevens.</p>
        </div>
      )}
      <div className="space-y-4">
        {items.map((loc, i) => (
          <div key={loc.id || i} className="rounded-lg border border-border p-3" data-testid={`locatie-rij-${i}`}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {VELDEN.map(([k, label]) => (
                <div key={k} className={k === "naam" ? "sm:col-span-3" : ""}>
                  <Label className="text-xs text-muted-foreground">{label}</Label>
                  <Input value={loc[k] || ""} disabled={!isAdmin} onChange={(e) => set(i, k, e.target.value)}
                    data-testid={`locatie-${i}-${k}`} />
                </div>
              ))}
            </div>
            {isAdmin && (
              <div className="flex justify-end mt-2">
                <Button variant="ghost" size="sm" className="text-destructive"
                  onClick={() => setItems(prev => prev.filter((_, idx) => idx !== i))}
                  data-testid={`locatie-verwijder-${i}`}>
                  <Trash2 className="h-4 w-4 mr-1" />Verwijderen
                </Button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground italic">Nog geen extra locaties.</p>}
      </div>
      {isAdmin && (
        <div className="mt-4 flex justify-between">
          <Button variant="outline" onClick={() => setItems(prev => [...prev, { ...LEEG }])} data-testid="locatie-toevoegen">
            <Plus className="h-4 w-4 mr-1" />Locatie toevoegen
          </Button>
          <Button onClick={save} disabled={saving} data-testid="locaties-save">
            <Save className="h-4 w-4 mr-2" />{saving ? "Opslaan…" : "Opslaan"}
          </Button>
        </div>
      )}
    </div>
  );
};
